import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import SnackBar from "../SnackBar";
import { Button, CardContent, IconButton, TextField, Tooltip } from "@material-ui/core";
import { DeleteRounded, GetApp, Print, AssignmentReturn } from "@material-ui/icons";
import { api_delete_many, api_post } from '../../utils/Api';
import { exportAvoirsPdf, exportFacturesPdf, printAvoirs, printFactures } from "../../views/Facture/facturePrintTemplate";
import { useStore } from "../../contexts/JWTAuthContext";
import { checkPermission } from "../../utils/acl/aclRouter";


const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(1),
    },
    content: {
        marginTop: theme.spacing(2),
    },
    style: {
        color: 'red',
        fontSize: 20
    },
}));

const ActionDeleteMany = (props) => {
    const { pageLink, data, noDeleteMany, deleteUrl, selectedItems, setSelectedItems, initializeData, keyValue } = props;
    const { user } = useStore();
    const routeDelete = window.location.pathname + '/delete';
    const [message, setAlertMessage] = useState('');
    const [severity, setAlertSeverity] = useState('success');
    const [openModal, setOpenModal] = useState(false);
    const [openSendModal, setOpenSendModal] = useState(false);
    const [openAvoirModal, setOpenAvoirModal] = useState(false);
    const [avoirReason, setAvoirReason] = useState('');
    const [avoirLoading, setAvoirLoading] = useState(false);
    const [openAlert, setOpenAlert] = React.useState(false);


    const handleModalClose = () => {
        setOpenModal(!openModal);
        if (selectedItems.length === 0) {
            setOpenModal(false);
        }
    };

    const handleSendModalClose = () => {
        setOpenSendModal(!openSendModal);
        if (selectedItems.length === 0) {
            setOpenSendModal(false);
        }
    };

    const handleAvoirModalClose = () => {
        if (avoirLoading) return;
        setOpenAvoirModal(!openAvoirModal);
        if (selectedItems.length === 0) {
            setOpenAvoirModal(false);
        }
    };

    const handleCloseAlert = () => {
        setOpenAlert(false);
    };

    const handleDeleteItems = () => {
        api_delete_many(deleteUrl, selectedItems, keyValue).then((data) => {
            if (data?.code !== 200 && data?.code !== 201) {
                setAlertSeverity('warning');
                setAlertMessage(data.message);
                setOpenAlert(true);
            }
            else {
                setAlertSeverity('success');
                setAlertMessage('item(s) deleted successfully');
                setOpenModal(false);
                setOpenAlert(true);
            }
            initializeData();
            setSelectedItems([]);
        })
    };

    const selectedCodes = useMemo(() => {
        if (!Array.isArray(selectedItems)) {
            return [];
        }

        return selectedItems.filter((v) => v !== undefined && v !== null && v !== "");
    }, [selectedItems]);

    const selectedCodeRows = useMemo(() => {
        const payload = data?.payload;
        if (!Array.isArray(payload) || !Array.isArray(selectedItems)) {
            return [];
        }
        const selectedSet = new Set(selectedItems.map((v) => String(v)));
        return payload.filter((row) => selectedSet.has(String(row?.id)));
    }, [data, selectedItems]);

    const selectedCodesTotalAmount = useMemo(() => {
        if (!Array.isArray(selectedCodeRows) || selectedCodeRows.length === 0) {
            return 0;
        }
        return selectedCodeRows.reduce((sum, row) => sum + Number(row?.amount ?? 0), 0);
    }, [selectedCodeRows]);

    const selectedFactureInvoices = useMemo(() => {
        const payload = data?.payload;
        if (!Array.isArray(payload) || !Array.isArray(selectedItems)) {
            return [];
        }
        const selectedSet = new Set(selectedItems.map((v) => String(v)));
        return payload.filter((row) => selectedSet.has(String(row?.id)));
    }, [data, selectedItems]);

    const selectedAvoirs = useMemo(() => {
        const payload = data?.payload;
        if (!Array.isArray(payload) || !Array.isArray(selectedItems)) {
            return [];
        }
        const selectedSet = new Set(selectedItems.map((v) => String(v)));
        return payload.filter((row) => selectedSet.has(String(row?.id)));
    }, [data, selectedItems]);

    const normalizeInvoiceToFacture = (invoice) => {
        const id = invoice?.id ?? Date.now();
        const number = invoice?.facture_number || String(id);
        const userName =
            String(invoice?.full_name || '').trim() ||
            `${invoice?.first_name || ''} ${invoice?.last_name || ''}`.trim() ||
            '---';
        const userId = invoice?.user_id ?? invoice?.user?.id ?? "";
        const taxIdentificationNumber = invoice?.tax_identification_number || "";
        const paymentMethod =
            invoice?.payment_method?.name ||
            invoice?.payment_method ||
            invoice?.paymentMethod ||
            "";
        const description = invoice?.description || "cours en ligne";
        const offerName = invoice?.group_name || '---';
        const offerStartDate = invoice?.affiliation_start_date || '';
        const offerEndDate = invoice?.affiliation_end_date || '';
        const netToPay = Number(invoice?.amount_paid ?? invoice?.amount_code ?? 0);
        const totalHt = Number(invoice?.subtotal_amount ?? 0);
        const totalTva = Number(invoice?.tax_amount ?? 0);
        const stamp = Number(invoice?.fiscal_stamp_amount ?? 0);
        const amountInWords = invoice?.total_amount_in_words || "";

        return {
            id,
            number,
            date: invoice?.created_at || invoice?.affiliation_start_date || '',
            userName,
            offerName,
            paymentMethod,
            offerStartDate,
            offerEndDate,
            items: [
                {
                    description,
                    quantity: 1,
                    unit: 'pcs',
                    unitPriceTtc: netToPay,
                    tva: 19,
                    totalTtc: netToPay,
                },
            ],
            client: {
                name: userName,
                id: userId,
                address: '',
                mf: taxIdentificationNumber ? String(taxIdentificationNumber) : '',
            },
            totalHt,
            baseTva: totalHt,
            totalTva,
            stamp,
            netToPay,
            amountInWords,
        };
    };

    const handleBulkExportFactures = () => {
        const factures = selectedFactureInvoices.map(normalizeInvoiceToFacture);
        exportFacturesPdf(factures);
    };

    const handleBulkPrintFactures = () => {
        const factures = selectedFactureInvoices.map(normalizeInvoiceToFacture);
        printFactures(factures);
    };

    const normalizeAvoirToDoc = (creditNote) => {
        const id = creditNote?.id ?? Date.now();
        const number = creditNote?.credit_note_number || String(id);
        const userName =
            String(creditNote?.full_name || '').trim() ||
            `${creditNote?.first_name || creditNote?.user?.name || ''} ${creditNote?.last_name || creditNote?.user?.last_name || ''
                }`.trim() ||
            '---';
        const userId = creditNote?.user?.id ?? '';
        const invoiceDate = creditNote?.invoice?.date_invoice || '';
        const paymentMethod =
            creditNote?.payment_method?.name ||
            creditNote?.payment_method ||
            creditNote?.paymentMethod ||
            creditNote?.invoice?.payment_method?.name ||
            creditNote?.invoice?.payment_method ||
            '';

        const offerStartDate = creditNote?.affiliation_start_date || creditNote?.invoice?.affiliation_start_date || '';
        const offerEndDate = creditNote?.affiliation_end_date || creditNote?.invoice?.affiliation_end_date || '';
        const offerName = creditNote?.invoice?.group_name || creditNote?.group_name || '---';
        const description = creditNote?.description || 'cours en ligne';

        const netToPay = Number(creditNote?.total_amount ?? 0);
        const totalHt = Number(creditNote?.subtotal_amount ?? 0);
        const totalTva = Number(creditNote?.tax_amount ?? 0);
        const stamp = Number(creditNote?.fiscal_stamp_amount ?? 0);
        const amountInWords = creditNote?.total_amount_in_words || '';

        return {
            id,
            number,
            date: creditNote?.created_at || '',
            invoiceDate,
            userName,
            offerName,
            paymentMethod,
            offerStartDate,
            offerEndDate,
            items: [
                {
                    description,
                    quantity: 1,
                    unit: 'pcs',
                    unitPriceTtc: netToPay,
                    tva: 19,
                    totalTtc: netToPay,
                },
            ],
            client: {
                name: userName,
                id: userId,
                address: '',
                mf: '',
            },
            totalHt,
            baseTva: totalHt,
            totalTva,
            stamp,
            netToPay,
            amountInWords,
        };
    };

    const handleBulkExportAvoirs = () => {
        const docs = selectedAvoirs.map(normalizeAvoirToDoc);
        exportAvoirsPdf(docs);
    };

    const handleBulkPrintAvoirs = () => {
        const docs = selectedAvoirs.map(normalizeAvoirToDoc);
        printAvoirs(docs);
    };

    const handleBulkAvoir = () => {
        const invoiceIds = selectedFactureInvoices
            .map((inv) => inv?.id)
            .filter((v) => v !== undefined && v !== null && v !== "");
        const reason = (avoirReason || '').trim();

        if (invoiceIds.length === 0 || !reason) {
            return;
        }

        setAvoirLoading(true);
        api_post("invoice-credit-notes/generate-from-invoices", { invoiceIds, reason })
            .then((resp) => {
                if (resp?.code !== 200 && resp?.code !== 201) {
                    setAlertSeverity('warning');
                    setAlertMessage(resp?.message || 'Failed to generate avoir');
                    setOpenAlert(true);
                    return;
                }
                setAlertSeverity('success');
                setAlertMessage('Avoir generated successfully');
                setOpenAvoirModal(false);
                setOpenAlert(true);
                if (typeof initializeData === 'function') {
                    initializeData();
                }
                setSelectedItems([]);
            })
            .catch(() => {
                setAlertSeverity('warning');
                setAlertMessage('Failed to generate avoir');
                setOpenAlert(true);
            })
            .finally(() => {
                setAvoirLoading(false);
            });
    };

    const handleSendCodesToFactures = () => {
        api_post('codes-to-be-invoiced', { codes: selectedCodes }).then((resp) => {
            if (resp?.code !== 200 && resp?.code !== 201) {
                setAlertSeverity('warning');
                setAlertMessage(resp?.message || 'Failed to send codes');
                setOpenAlert(true);
                return;
            }
            setAlertSeverity('success');
            setAlertMessage('Code(s) sent successfully');
            setOpenSendModal(false);
            setOpenAlert(true);
            if (typeof initializeData === 'function') {
                initializeData();
            }
            setSelectedItems([]);
        }).catch(() => {
            setAlertSeverity('warning');
            setAlertMessage('Failed to send codes');
            setOpenAlert(true);
        });
    };

    const classes = useStyles();

    return (
        <div className={classes.root}>
            {selectedItems.length > 0 && !noDeleteMany && user && checkPermission(routeDelete, user.roles, user?.admin_groups) && (
                <CardContent>
                    Delete many items :
                    <Button
                        color="primary"
                        size="large"
                        onClick={handleModalClose}
                        startIcon={<DeleteRounded />}>
                        Delete
                    </Button>
                </CardContent>)}

            {pageLink === '/finance/cash-payments' && selectedItems.length > 0 && selectedCodes.length > 0 && (
                <CardContent>
                    Send to factures :
                    <Button
                        color="primary"
                        size="large"
                        onClick={handleSendModalClose}
                    >
                        Send
                    </Button>
                </CardContent>
            )}

            {pageLink === '/invoice-credit-notes' && selectedAvoirs.length > 0 && (
                <CardContent>
                    Avoirs :
                    <Tooltip title={`Export (${selectedAvoirs.length})`}>
                        <IconButton onClick={handleBulkExportAvoirs}>
                            <GetApp />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={`Imprimer (${selectedAvoirs.length})`}>
                        <IconButton onClick={handleBulkPrintAvoirs}>
                            <Print />
                        </IconButton>
                    </Tooltip>
                </CardContent>
            )}

            {pageLink === '/finance/factures' && selectedFactureInvoices.length > 0 && (
                <CardContent>
                    Factures :
                    <Tooltip title={`Export (${selectedFactureInvoices.length})`}>
                        <IconButton onClick={handleBulkExportFactures}>
                            <GetApp />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={`Imprimer (${selectedFactureInvoices.length})`}>
                        <IconButton onClick={handleBulkPrintFactures}>
                            <Print />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={`Avoir (${selectedFactureInvoices.length})`}>
                        <IconButton onClick={() => { setAvoirReason(''); setOpenAvoirModal(true); }}>
                            <AssignmentReturn />
                        </IconButton>
                    </Tooltip>
                </CardContent>
            )}

            <Dialog
                open={openAvoirModal}
                onClose={handleAvoirModalClose}
                aria-labelledby="avoir-dialog-title"
                aria-describedby="avoir-dialog-description">
                <DialogTitle id="avoir-dialog-title">Generate Avoir</DialogTitle>
                <DialogContent>
                    <DialogContentText id="avoir-dialog-description">
                        Please provide a reason for generating this avoir.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        variant="outlined"
                        label="Reason"
                        value={avoirReason}
                        onChange={(e) => setAvoirReason(e.target.value)}
                        disabled={avoirLoading}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAvoirModalClose} color="primary" disabled={avoirLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleBulkAvoir}
                        color="primary"
                        autoFocus
                        disabled={avoirLoading || !String(avoirReason || '').trim()}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={openModal}
                onClose={handleModalClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">Delete item(s) Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete these <span className={classes.style}>{selectedItems.length}</span> item(s) ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleModalClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => handleDeleteItems()} color="primary" autoFocus>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openSendModal}
                onClose={handleSendModalClose}
                aria-labelledby="send-codes-dialog-title"
                aria-describedby="send-codes-dialog-description">
                <DialogTitle id="send-codes-dialog-title">Send to factures Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText id="send-codes-dialog-description">
                        Are you sure you want to send these <span className={classes.style}>{selectedCodes.length}</span> code(s) to factures?
                        <br />
                        Total amount: <span className={classes.style}>{selectedCodesTotalAmount}</span> DT
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSendModalClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => handleSendCodesToFactures()} color="primary" autoFocus>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            <SnackBar
                open={openAlert}
                message={message}
                severity={severity}
                handleClose={handleCloseAlert}
                autoHideDuration={6000}
            />
        </div>
    );
};

export default ActionDeleteMany;
